import React from "react";
import { Link } from "react-router-dom";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { Counter } from "@/components/motion/Counter";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const BLOCK_META = [
  { status: "LIVE", image: "/products/p1.png" },
  { status: "LIVE", image: "/products/p2.png" },
  { status: "LIVE", image: "/products/p3.png" },
  { status: "LIVE", image: "/products/p4.png" },
  { status: "IN_PARTNERSHIP", image: "/products/p5.png" },
];

const ARCH_META = [
  { key: "evm-multi-chain", status: "LIVE" },
  { key: "web3auth-keyless-onboarding", status: "LIVE" },
  { key: "ipfs-document-storage", status: "LIVE" },
  { key: "smart-contract-escrow", status: "LIVE" },
  { key: "vechain-nfc-tags", status: "PILOT" },
  { key: "helium-gps-iot", status: "PILOT" },
];

const STAT_META = [
  { to: 400, format: (n) => `$${Math.round(n)}M+`, testId: "tok-stat-value" },
  { to: 8, format: (n) => `${Math.round(n)}`, testId: "tok-stat-deals" },
  { to: 3, format: (n) => `${Math.round(n)}`, testId: "tok-stat-chains" },
  { to: 30, format: (n) => `${Math.round(n)} days`, testId: "tok-stat-deploy" },
];

function Hero() {
  const { t } = useT("platformTokenization");
  const locale = useLocale();
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="tokenization-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="tokenization-cta-primary"
            >
              {t("hero.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Blocks() {
  const { t } = useT("platformTokenization");
  const blocks = t("blocks", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-16 md:py-24"
      data-testid="tokenization-blocks"
    >
      <div className="flex flex-col gap-6">
        {blocks.map((b, i) => (
          <Reveal key={i}>
            <Card
              variant="glass"
              className="overflow-hidden p-0"
              data-testid={`tok-block-${i + 1}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr]">
                <div className="p-8 md:p-10">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-2xl font-bold tracking-tight text-ink-primary">
                      {b.name}
                    </h2>
                    <Badge status={BLOCK_META[i]?.status} onDark />
                  </div>
                  <p className="mt-4 text-base leading-normal text-ink-secondary">
                    {b.body}
                  </p>
                  {b.proof && (
                    <p className="mt-4 text-sm italic text-ink-tertiary">
                      {b.proof}
                    </p>
                  )}
                </div>
                <div className="relative min-h-[220px] overflow-hidden border-t border-hairline bg-surface-elevated/40 md:border-l md:border-t-0">
                  <img
                    src={BLOCK_META[i]?.image}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover object-center opacity-90"
                    data-testid={`tok-block-${i + 1}-image`}
                  />
                </div>
              </div>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Architecture() {
  const { t } = useT("platformTokenization");
  const items = t("architecture.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="tokenization-architecture"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("architecture.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("architecture.h2")}
        </MaskReveal>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr]">
        <Stagger className="grid grid-cols-1 gap-4 md:grid-cols-2" gap={70}>
          {items.map((a, i) => (
            <StaggerItem key={i}>
              <div
                className="h-full rounded-[16px] border border-hairline bg-surface-elevated/40 p-5"
                data-testid={`tok-arch-${ARCH_META[i]?.key || i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-bold text-ink-primary">
                    {a.name}
                  </h3>
                  <Badge status={ARCH_META[i]?.status} onDark />
                </div>
                <p className="mt-3 text-xs text-ink-secondary">{a.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={200}>
          <div
            className="relative aspect-square w-full rounded-[16px] border border-hairline bg-surface-raised/30 p-6"
            aria-hidden
            data-testid="tok-arch-diagram"
          >
            <svg viewBox="0 0 300 300" className="h-full w-full">
              <defs>
                <linearGradient id="ring" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#7200DB" />
                  <stop offset="100%" stopColor="#3B1861" />
                </linearGradient>
              </defs>
              <g fill="none" stroke="rgba(237,241,245,0.14)" strokeWidth="1">
                <circle cx="150" cy="150" r="130" />
                <circle cx="150" cy="150" r="90" />
                <circle cx="150" cy="150" r="50" />
              </g>
              <g fontFamily="'JetBrains Mono', ui-monospace, monospace" fontSize="10" fill="#EDF1F5" textAnchor="middle">
                <text x="150" y="30" fill="rgba(237,241,245,0.72)">{t("architecture.labels.user")}</text>
                <text x="150" y="72" fill="rgba(237,241,245,0.72)">{t("architecture.labels.platform")}</text>
                <text x="150" y="120" fill="#7200DB">{t("architecture.labels.rails")}</text>
                <text x="150" y="155" fill="#EDF1F5" fontWeight="600" fontSize="12">{t("architecture.labels.core")}</text>
              </g>
              <g fill="none" stroke="url(#ring)" strokeWidth="1.5">
                <path d="M150 20 A 130 130 0 0 1 280 150" opacity="0.7" />
                <path d="M20 150 A 130 130 0 0 1 150 20" opacity="0.4" />
              </g>
            </svg>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Principles() {
  const { t } = useT("platformTokenization");
  const items = t("principles.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="tokenization-principles"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("principles.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("principles.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3" gap={70}>
        {items.map((p, i) => (
          <StaggerItem key={i}>
            <div
              className="relative h-full rounded-[16px] border border-hairline bg-surface-raised/40 p-6"
              data-testid={`tok-principle-${i + 1}`}
            >
              <div
                className="absolute inset-x-0 top-0 h-[2px] rounded-t-[16px] bg-frac-violet"
                aria-hidden
              />
              <h3 className="text-base font-bold text-ink-primary">
                {p.name}
              </h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {p.body}
              </p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Proof() {
  const { t } = useT("platformTokenization");
  const stats = t("stats", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="tokenization-proof"
    >
      <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" gap={70}>
        {STAT_META.map((s, i) => (
          <StaggerItem key={s.testId}>
            <Card variant="glass" className="h-full">
              <div
                className="text-4xl font-bold leading-none text-ink-primary sm:text-5xl"
                data-testid={s.testId}
              >
                <Counter to={s.to} format={s.format} duration={1200} />
              </div>
              <p className="mt-4 text-sm leading-normal text-ink-secondary">
                {stats[i]?.label}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platformTokenization");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="tokenization-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={300}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="tokenization-final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PlatformTokenization() {
  return (
    <>
      <PageSeo
        title="The Tokenization Terminal — Production Rails Since 2022"
        description="Asset Owners' Portal, IAO Portal, DEX, Supply Chain and Card Payments. Production tokenization infrastructure across Polygon, BNB Smart Chain and Ethereum."
        path="/platform/tokenization"
        image="/og/platform-tokenization.png"
      />
      <div data-testid="page-platform-tokenization">
        <Hero />
        <Blocks />
        <Architecture />
        <Principles />
        <Proof />
        <FinalCta />
      </div>
    </>
  );
}
